import React, {useEffect, useMemo, useRef, useState} from 'react';
import {HStack, VStack} from "native-base";
import {Colors} from "../../../../styles/Colors";
import {LOW_PRIORITY, richTextActions} from "../helper";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {
    $getSelection, $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND, FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    REDO_COMMAND, SELECTION_CHANGE_COMMAND,
    UNDO_COMMAND,
    $isElementNode
} from "lexical";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {GlobalConsts} from "../../../../styles/GlobalConsts";
import {light, regular} from "@fortawesome/fontawesome-svg-core/import.macro";
import {mergeRegister} from "@lexical/utils";
import {Select} from "antd";
import {isMobile} from "../../../../services/Helpers";
import { $wrapNodes } from "@lexical/selection";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import {isRTL, t} from "../../../../services/i18n";
import HighlighterPlugin from "./HighlighterPlugin";
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, $isListNode } from '@lexical/list';
import Alignment from "../Alignment";
import {INSERT_TABLE_COMMAND} from "@lexical/table";
import {openModal, SETTINGS_VALUE_MODAL} from "../../../../redux/modalManagerSlice";
import {TEXT_EDITOR_TABLE_INIT} from "../../../../Configs/SettingsValueModalOptions";
import {useDispatch, useSelector} from "react-redux";

const ToolbarPlugin = (props) => {
    const { } = props;
    const settingsValueModalRes = useSelector(state => state.modalManager.settingsValueModal.processRes)
    const [editor] = useLexicalComposerContext()
    const toolbarRef = useRef(null);
    const [disableMap, setDisableMap] = useState({
        [richTextActions.Undo]: true,
        [richTextActions.Redo]: true,
    });
    const [selectionMap, setSelectionMap] = useState({});
    const dispatch = useDispatch()

    const headingOptions = useMemo(() => [
        {value: 'p', label: t(`normal-text${isMobile ? '-mobile' : ''}`)},
        {value: 'h1', label: t(`heading-1${isMobile ? '-mobile' : ''}`)},
        {value: 'h2', label: t(`heading-2${isMobile ? '-mobile' : ''}`)},
        {value: 'h3', label: t(`heading-3${isMobile ? '-mobile' : ''}`)},
    ], []);

    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(SELECTION_CHANGE_COMMAND, (payload) => updateSelected(), LOW_PRIORITY),
            editor.registerCommand(CAN_UNDO_COMMAND, (payload) => updateDisableMap(richTextActions.Undo, !payload), LOW_PRIORITY),
            editor.registerCommand(CAN_REDO_COMMAND, (payload) => updateDisableMap(richTextActions.Redo, !payload), LOW_PRIORITY),
        )
    }, []);

    useEffect(() => {
        if(settingsValueModalRes?.type === TEXT_EDITOR_TABLE_INIT) {
            initTable(settingsValueModalRes.values)
        }
    }, [settingsValueModalRes]);

    const updateDisableMap = (actionId, payload) => {
        setDisableMap((prev) => ({...prev, [actionId]: payload}))
        return false
    }

    const updateSelected = () => {
        const selection = $getSelection()
        if($isRangeSelection(selection)) {
            const newSelectionMap = {
                [richTextActions.Bold]: selection.hasFormat("bold"),
                [richTextActions.Italics]: selection.hasFormat("italic"),
                [richTextActions.Underline]: selection.hasFormat("underline"),
                [richTextActions.Heading]: getHeadingSelection(selection),
                [richTextActions.Bullet]: getListSelection(selection),
                [richTextActions.NumberList]: getListSelection(selection, 'number'),
                [richTextActions.Alignment]: getAlignmentSelection(selection),
            };
            setSelectionMap(newSelectionMap);
        }
        return false
    }

    const getHeadingSelection = (selection) => {
        const anchorNode = selection.anchor.getNode();
        const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
        return $isHeadingNode(element) ? element.getTag() : 'p'
    }

    const getListSelection = (selection, type = 'bullet') => {
        const anchorNode = selection.anchor.getNode();
        const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
        return $isListNode(element) && element.getListType() === type;
    }

    const getAlignmentSelection = (selection) => {
        const anchorNode = selection.anchor.getNode();
        const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
        const defaultAlignment = isRTL() ? 'right' : 'left'
        let currentAlignment = defaultAlignment;

        if ($isElementNode(element)) {
            const elementFormat = element.getFormatType();
            currentAlignment = elementFormat ?? defaultAlignment;
        }
        return currentAlignment
    }

    const getSelectedStyle = (actionId) => {
        if(selectionMap[actionId]) return 'active'
        return ''
    }

    const updateHeading = (heading) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $wrapNodes(selection, () => $createHeadingNode(heading));
                setSelectionMap(prev => ({...prev, [richTextActions.Heading]: heading}))
            }
        });
    };

    const toggleList = (type = 'bullet') => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                const anchorNode = selection.anchor.getNode();
                const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
                const isList = $isListNode(element) || $isListNode(element.getParent())
                const actionType = type === 'bullet' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND
                editor.dispatchCommand((isList && element.getListType() === type) ? REMOVE_LIST_COMMAND : actionType, undefined);
            }
        });
    };

    const updateAlignment = (alignment) => {
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment)
        setSelectionMap(prev => ({...prev, [richTextActions.Alignment]: alignment}))
    }

    const addTable = () => {
        dispatch(openModal({ modalName: SETTINGS_VALUE_MODAL, props: {type: TEXT_EDITOR_TABLE_INIT}}))
    }

    const initTable = ({columns, rows, headerRow, headerColumn}) => {
        editor.dispatchCommand(INSERT_TABLE_COMMAND, {
            rows: rows,
            columns: columns,
            includeHeaders: {
                rows: headerRow ?? false,
                columns: headerColumn ?? false
            }
        });
    }

    return (
    <VStack ref={toolbarRef} p={'0.3rem'} style={{borderBottom: `1px solid ${Colors.borderColor}`}}>
        <HStack bgColor={Colors.newDesign.menuBar} px={'0.7rem'} py={'0.4rem'} space={'0.7rem'} borderRadius={'5px'} width={'fit-content'}>
            {!isMobile &&
            <div onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} className={`simple-action-button ${disableMap[richTextActions.Undo] ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={light('rotate-left')} color={Colors.newDesign.mainText} fontSize={GlobalConsts.iconSize.XS}/>
            </div>}
            {!isMobile &&
                <div onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} className={`simple-action-button ${disableMap[richTextActions.Redo] ? 'disabled' : ''}`}>
                <FontAwesomeIcon icon={regular('rotate-right')} color={Colors.newDesign.mainText} fontSize={GlobalConsts.iconSize.XS}/>
            </div>}
            {!isMobile && <div className={'divider'}/>}
            <Select options={headingOptions}
                    touchUi={isMobile}
                    defaultValue={'p'}
                    value={selectionMap[richTextActions.Heading]}
                    onChange={(selection) => updateHeading(selection)}
                    style={{width: isMobile ? '6.5rem' : '9rem', height: '28px', alignSelf: 'center'}}
                    popupClassName={'editor-select'}
                    suffixIcon={<FontAwesomeIcon icon={light('chevron-down')} color={Colors.newDesign.mainText} fontSize={GlobalConsts.iconSize.XSS}/>}
            />
            <div className={'divider'}/>
            <div onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')} className={`bold-btn simple-action-button ${getSelectedStyle(richTextActions.Bold)}`}>B</div>
            <div onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')} className={`italic-btn simple-action-button ${getSelectedStyle(richTextActions.Italics)}`}>I</div>
            <div onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')} className={`underline-btn simple-action-button ${getSelectedStyle(richTextActions.Underline)}`}>U</div>
            {!isMobile && <HighlighterPlugin/>}
            <div className={'divider'}/>
            <div onClick={() => toggleList()} className={`simple-action-button ${selectionMap[richTextActions.Bullet] ? 'active' : ''}`}>
                <FontAwesomeIcon icon={light('list-ul')} color={Colors.newDesign.mainText} fontSize={GlobalConsts.iconSize.XS}/>
            </div>
            <div onClick={() => toggleList('number')} className={`simple-action-button ${selectionMap[richTextActions.NumberList] ? 'active' : ''}`}>
                <FontAwesomeIcon icon={light('list-ol')} color={Colors.newDesign.mainText} fontSize={GlobalConsts.iconSize.XS}/>
            </div>
            {!isMobile && <div className={'divider'}/>}
            {!isMobile &&
                <>
                <div onClick={addTable} className={`simple-action-button`}>
                    <FontAwesomeIcon icon={regular('table')} color={Colors.newDesign.mainText} fontSize={GlobalConsts.iconSize.XS}/>
                </div>
                <div className={'divider'}/>
                </>
            }
            {!isMobile && <Alignment selection={selectionMap[richTextActions.Alignment]}
                                     onSelection={(selection) => updateAlignment(selection)}/>}
        </HStack>
    </VStack>
    );
};

export default ToolbarPlugin;